import type { LocalProfile } from "./auth-types";

const LEGACY_GOOGLE_ID = "ccs.auth.id_token";
const GOOGLE_ID_TOKEN = "ccs.auth.google_id_token";
const GOOGLE_ACCESS_TOKEN = "ccs.auth.google_access_token";
const APP_JWT = "ccs.auth.app_jwt";
const PROFILE = "ccs.auth.profile";
const IMPERSONATION_RESUME_JWT = "ccs.auth.impersonation_resume_jwt";
const IMPERSONATION_META = "ccs.auth.impersonation_meta";
/** Holds verifier + state across the Google redirect round-trip (same tab only). */
export const OAUTH_PKCE_KEY = "ccs.oauth.pkce";

const PERSISTENT_AUTH_KEYS = [
  GOOGLE_ID_TOKEN,
  LEGACY_GOOGLE_ID,
  GOOGLE_ACCESS_TOKEN,
  APP_JWT,
  PROFILE,
  IMPERSONATION_RESUME_JWT,
  IMPERSONATION_META,
] as const;

export interface PkceBundle {
  codeVerifier: string;
  state: string;
}

export interface ImpersonationMeta {
  targetUserId: string;
  targetEmail: string;
  targetRole: "teacher" | "student";
  targetGoogleSub: string;
  impersonatorSub: string;
}

/** Read auth from localStorage, falling back to legacy sessionStorage (read-only). */
function readPersistentAuth(key: string): string | null {
  const fromLocal = localStorage.getItem(key);
  if (fromLocal !== null) return fromLocal;
  return sessionStorage.getItem(key);
}

function writePersistentAuth(key: string, value: string): void {
  localStorage.setItem(key, value);
  sessionStorage.removeItem(key);
}

function removePersistentAuth(key: string): void {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
}

/** One-time upgrade for tabs that logged in before auth moved to localStorage. */
export function migrateAuthStorageFromSession(): void {
  for (const key of PERSISTENT_AUTH_KEYS) {
    const fromSession = sessionStorage.getItem(key);
    if (fromSession === null) continue;
    if (localStorage.getItem(key) === null) {
      localStorage.setItem(key, fromSession);
    }
    sessionStorage.removeItem(key);
  }
}

export function savePkceBundle(bundle: PkceBundle): void {
  sessionStorage.setItem(OAUTH_PKCE_KEY, JSON.stringify(bundle));
}

export function loadPkceBundle(): PkceBundle | null {
  const raw = sessionStorage.getItem(OAUTH_PKCE_KEY);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as PkceBundle;
    if (typeof v.codeVerifier === "string" && typeof v.state === "string") return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function clearPkceBundle(): void {
  sessionStorage.removeItem(OAUTH_PKCE_KEY);
}

/** Legacy Google-only flow (no backend session exchange). */
export function saveGoogleTokens(idToken: string, accessToken?: string): void {
  writePersistentAuth(GOOGLE_ID_TOKEN, idToken);
  removePersistentAuth(LEGACY_GOOGLE_ID);
  if (accessToken) writePersistentAuth(GOOGLE_ACCESS_TOKEN, accessToken);
  else removePersistentAuth(GOOGLE_ACCESS_TOKEN);
}

export function loadGoogleIdToken(): string | null {
  return readPersistentAuth(GOOGLE_ID_TOKEN) ?? readPersistentAuth(LEGACY_GOOGLE_ID);
}

export function loadGoogleAccessToken(): string | null {
  return readPersistentAuth(GOOGLE_ACCESS_TOKEN);
}

export function saveAppJwt(token: string): void {
  writePersistentAuth(APP_JWT, token);
}

export function loadAppJwt(): string | null {
  return readPersistentAuth(APP_JWT);
}

export function saveImpersonationResumeJwt(token: string): void {
  writePersistentAuth(IMPERSONATION_RESUME_JWT, token);
}

export function loadImpersonationResumeJwt(): string | null {
  return readPersistentAuth(IMPERSONATION_RESUME_JWT);
}

export function clearImpersonationResumeJwt(): void {
  removePersistentAuth(IMPERSONATION_RESUME_JWT);
}

export function saveImpersonationMeta(meta: ImpersonationMeta): void {
  writePersistentAuth(IMPERSONATION_META, JSON.stringify(meta));
}

export function loadImpersonationMeta(): ImpersonationMeta | null {
  const raw = readPersistentAuth(IMPERSONATION_META);
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as ImpersonationMeta;
    if (
      typeof v.targetUserId === "string" &&
      typeof v.targetEmail === "string" &&
      (v.targetRole === "teacher" || v.targetRole === "student") &&
      typeof v.targetGoogleSub === "string" &&
      typeof v.impersonatorSub === "string"
    ) {
      return v;
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function clearImpersonationMeta(): void {
  removePersistentAuth(IMPERSONATION_META);
}

export function clearImpersonationSession(): void {
  clearImpersonationResumeJwt();
  clearImpersonationMeta();
}

export function clearAuthStorage(): void {
  for (const key of PERSISTENT_AUTH_KEYS) {
    removePersistentAuth(key);
  }
  clearPkceBundle();
}

export function saveProfile(profile: LocalProfile): void {
  writePersistentAuth(PROFILE, JSON.stringify(profile));
}

export function loadProfile(): LocalProfile | null {
  const raw = readPersistentAuth(PROFILE);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LocalProfile;
  } catch {
    return null;
  }
}

/** Keys whose changes should refresh auth state in other tabs. */
export const AUTH_STORAGE_SYNC_KEYS: readonly string[] = PERSISTENT_AUTH_KEYS;

if (typeof window !== "undefined") {
  try {
    migrateAuthStorageFromSession();
  } catch {
    /* storage may be unavailable (private mode, blocked, etc.) */
  }
}
